import { useSelector } from "react-redux";
import "../businessAnalysis/businessAnalysis.css";
import "@src/components/compliance/compliance.css";
import "@src/components/document/document.css";
import { useMemo, useRef, useState } from "react";
import { Button, Dropdown, Input, Select, Table, Tabs, Tag, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faSearch } from "@fortawesome/free-solid-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { useTenantFeatureFlags } from "@src/hooks/useTenantFeatureFlags";
import { getComplianceDocumentsQuery, getComplianceOverviewQuery } from "@src/util/query/compliance.query";
import { exportComplianceAuditMutation, finalizeDocumentUploadMutation, issueToolboxTalkNowMutation, newDocumentVersionMutation } from "@src/util/mutation/compliance.mutation";
import { ArchiveDocumentMutation } from "@src/util/mutation/document.mutation";
import { RecipientsDrawer } from "@src/components/compliance/recipientsDrawer";
import { ComplianceDocumentDrawer } from "@src/components/compliance/complianceDocument.drawer";
import { describeAssignment, formatDate, METHOD_LABEL, RECURRENCE_LABEL, runAuditExport, summariseAudience, uploadThenFinalize } from "@src/components/compliance/complianceHelpers";
import { ToolboxTalkDrawer } from "@src/components/compliance/toolboxTalk.drawer";
import { RestoreDocumentModal } from "@src/components/compliance/restoreDocument.modal";
import { PageNotFound } from "@src/pages/unAuthroizedAccess/pageNotFound.page";
import { CaretDownFilled } from "@ant-design/icons";

const RECORD_ROUTE = {
    Site: "site-detail",
    Customer: "customer-detail",
    SiteGroup: "sitegroup-detail",
    Branch: "branch-detail",
    Employee: "employee-detail",
};

const APPLIES_FILTERS = [
    { value: "all", label: "Everything" },
    { value: "Compliance", label: "Company-wide" },
    { value: "Site", label: "Sites" },
    { value: "Customer", label: "Customers" },
    { value: "SiteGroup", label: "Site groups" },
    { value: "Branch", label: "Branches" },
    { value: "Employee", label: "Employees" },
];

const Progress = ({ stats, onClick }) => {
    const pct = stats?.total ? Math.round((stats.acknowledged / stats.total) * 100) : 0;
    return (
        <Tooltip title={stats?.total ? `${stats.acknowledged} signed, ${stats.pending} outstanding${stats.overdue ? `, ${stats.overdue} overdue` : ""}` : "No one to send it to yet"}>
            <span className="cmp-progress" onClick={onClick}>
                <span className="cmp-progress-bar"><span className={`cmp-progress-fill${stats?.total && pct === 100 ? " done" : stats?.overdue ? " late" : ""}`} style={{ width: `${pct}%` }} /></span>
                <span className="cmp-progress-text">{stats?.acknowledged || 0}/{stats?.total || 0}</span>
            </span>
        </Tooltip>
    );
};

export const CompliancePage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { complianceEnabled: tenantHasCompliance, loading: flagsLoading } = useTenantFeatureFlags();
    const { authenticatedUserDetail } = useSelector((state) => state.userAuthReducer);
    // the tenant must have the module and this user's role must not be denied it
    const complianceEnabled = tenantHasCompliance && authenticatedUserDetail?.permission?.Compliance?.access !== false;

    const [tab, setTab] = useState("documents");
    const [search, setSearch] = useState("");
    const [applies, setApplies] = useState("all");
    const [recipientsDocId, setRecipientsDocId] = useState(null);
    const [drawer, setDrawer] = useState({ open: false, editDoc: null });
    const versionInputRef = useRef();
    const [versionTarget, setVersionTarget] = useState(null);
    const [uploadingVersion, setUploadingVersion] = useState(false);

    const { data: overviewData, loading: overviewLoading, refetch: refetchOverview } = useQuery(getComplianceOverviewQuery, {
        skip: !complianceEnabled,
        fetchPolicy: "cache-and-network",
    });
    const { data: docsData, loading: docsLoading, refetch: refetchDocs } = useQuery(getComplianceDocumentsQuery, {
        variables: { filter: { kind: "document" } },
        skip: !complianceEnabled,
        fetchPolicy: "cache-and-network",
    });
    const { data: talksData, loading: talksLoading, refetch: refetchTalks } = useQuery(getComplianceDocumentsQuery, {
        variables: { filter: { kind: "toolboxTalk" } },
        skip: !complianceEnabled,
        fetchPolicy: "cache-and-network",
    });
    // archived by hand, documents and toolbox talks (the latest version / issue of each) - with
    // whether and how each can come back
    const { data: archivedData, loading: archivedLoading, refetch: refetchArchived } = useQuery(getComplianceDocumentsQuery, {
        variables: { filter: { archived: true } },
        skip: !complianceEnabled,
        fetchPolicy: "cache-and-network",
    });
    const [restoreDoc, setRestoreDoc] = useState(null);
    const [issueTalkNow, { loading: issuingTalk }] = useMutation(issueToolboxTalkNowMutation);
    const [talkDrawer, setTalkDrawer] = useState({ open: false, editTalk: null });
    const [newDocumentVersion] = useMutation(newDocumentVersionMutation);
    const [finalizeUpload] = useMutation(finalizeDocumentUploadMutation);
    const [archiveDocument] = useMutation(ArchiveDocumentMutation);
    const [exportAudit, { loading: exporting }] = useMutation(exportComplianceAuditMutation);

    const overview = overviewData?.getComplianceOverview?.response;
    const totals = overview?.totals;
    const documents = docsData?.getComplianceDocuments?.response || [];
    const talks = talksData?.getComplianceDocuments?.response || [];
    const archivedDocs = archivedData?.getComplianceDocuments?.response || [];

    const refetchAll = () => Promise.all([refetchOverview(), refetchDocs(), refetchTalks(), refetchArchived()]);

    const issueNow = async (talk) => {
        const result = await Swal.fire({
            title: `Send ${talk.title} again now?`,
            text: "A new issue goes out and everyone it applies to is asked to acknowledge it again.",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#fb8c5f",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Send new issue",
        });
        if (!result.isConfirmed) return;
        try {
            const res = await issueTalkNow({ variables: { documentId: talk._id } });
            await refetchAll();
            notify(`Issue ${talk.version + 1} sent - ${describeAssignment(res?.data?.issueToolboxTalkNow?.response).toLowerCase()}`);
        } catch (err) {
            notify(err.message, true);
        }
    };
    const notify = (message, error = false) => dispatch(setNotification({ error, notificationState: true, message }));

    const rows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return documents
            .filter((doc) => applies === "all" || doc.objectType === applies)
            .filter((doc) => !needle || `${doc.title} ${doc.typeName} ${doc.appliesTo?.name}`.toLowerCase().includes(needle))
            .map((doc) => ({ ...doc, key: doc._id }));
    }, [documents, search, applies]);
    const archivedRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        return archivedDocs
            .filter((doc) => applies === "all" || doc.objectType === applies)
            .filter((doc) => !needle || `${doc.title} ${doc.typeName} ${doc.appliesTo?.name} ${doc.archivedBy}`.toLowerCase().includes(needle))
            .map((doc) => ({ ...doc, key: doc._id }));
    }, [archivedDocs, search, applies]);

    const startNewVersion = (doc) => {
        setVersionTarget(doc);
        versionInputRef.current.value = "";
        versionInputRef.current.click();
    };

    const onVersionFile = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !versionTarget) return;
        try {
            setUploadingVersion(true);
            const res = await newDocumentVersion({ variables: { documentId: versionTarget._id, input: { fileName: file.name } } });
            const { url, documentId, version } = res?.data?.newDocumentVersion?.response || {};
            const result = await uploadThenFinalize({ url, documentId, file, finalize: finalizeUpload });
            await refetchAll();
            notify(`v${version} of ${versionTarget.title} uploaded - ${describeAssignment(result).toLowerCase()} to sign again`);
        } catch (err) {
            notify(err.message, true);
        } finally {
            setUploadingVersion(false);
            setVersionTarget(null);
        }
    };

    const archive = async (doc) => {
        const result = await Swal.fire({
            title: `Archive ${doc.title}?`,
            text: "Officers who haven't acknowledged it yet will no longer be asked to. Signatures already given stay on record.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, archive it",
        });
        if (!result.isConfirmed) return;
        try {
            await archiveDocument({ variables: { documentId: doc._id } });
            await refetchAll();
            notify("Document archived");
        } catch (err) {
            notify(err.message, true);
        }
    };

    const appliesToCell = (doc) => {
        if (doc.objectType === "Compliance") {
            return (
                <div className="cmp-person">
                    <span>Company-wide</span>
                    <span className="cmp-muted">{summariseAudience(doc.audience)}</span>
                </div>
            );
        }
        const route = RECORD_ROUTE[doc.objectType];
        return (
            <div className="cmp-person">
                {route ? <button className="cmp-link" onClick={() => navigate(`/user/${route}/${doc.entityId}`)}>{doc.appliesTo?.name}</button> : <span>{doc.appliesTo?.name}</span>}
                <span className="cmp-muted">{doc.appliesTo?.label}</span>
            </div>
        );
    };

    const documentColumns = [
        {
            title: "Document",
            dataIndex: "title",
            render: (v, doc) => (
                <div className="cmp-person">
                    {/* the name opens who it went to and the versions */}
                    <span className="cmp-person-name">
                        <button className="cmp-link" onClick={() => setRecipientsDocId(doc._id)} data-doc-name>{v}</button>
                        {doc.version > 1 ? <Tag style={{ marginLeft: 4 }}>v{doc.version}</Tag> : null}
                    </span>
                    <span className="cmp-muted">{doc.typeName && doc.typeName !== v ? doc.typeName : doc.fileName}</span>
                </div>
            ),
        },
        { title: "Applies to", dataIndex: "appliesTo", render: (_, doc) => appliesToCell(doc) },
        { title: "Confirm by", dataIndex: "acknowledgementMethod", render: (v) => METHOD_LABEL[v] || "--" },
        { title: "Signed", dataIndex: "stats", render: (stats, doc) => <Progress stats={stats} onClick={() => setRecipientsDocId(doc._id)} /> },
        { title: "Overdue", dataIndex: "overdue", render: (_, doc) => doc.stats?.overdue ? <span className="cmp-overdue">{doc.stats.overdue}</span> : <span className="cmp-muted">0</span> },
        {
            title: "Review by",
            dataIndex: "reviewDate",
            render: (v, doc) => v ? <span className={doc.reviewDue ? "cmp-overdue" : ""}>{formatDate(v)}</span> : <span className="cmp-muted">--</span>,
        },
        { title: "Uploaded", dataIndex: "uploadedAt", render: (v, doc) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{doc.uploadedBy}</span></div> },
        {
            title: "",
            dataIndex: "actions",
            align: "right",
            // every action in one menu (⋮), like a record's Documents tab
            render: (_, doc) => (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        menu={{
                            items: [
                                { key: "recipients", label: "Recipients" },
                                { key: "preview", label: "Preview" },
                                { type: "divider" },
                                { key: "version", label: uploadingVersion && versionTarget?._id === doc._id ? "Uploading new version..." : "Upload new version", disabled: uploadingVersion },
                                ...(doc.objectType === "Compliance" ? [{ key: "edit", label: "Edit" }] : []),
                                { type: "divider" },
                                { key: "archive", label: "Archive" },
                            ],
                            onClick: ({ key }) => {
                                if (key === "recipients") setRecipientsDocId(doc._id);
                                if (key === "preview") window.open(window.location.origin + "/user/preview?object=" + encodeURIComponent(doc.fileKey), "_blank");
                                if (key === "version") startNewVersion(doc);
                                if (key === "edit") setDrawer({ open: true, editDoc: doc });
                                if (key === "archive") archive(doc);
                            },
                        }}
                    >
                        <button type="button" className="doc-row-menu" aria-label="Document actions" data-doc-actions>
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                    </Dropdown>
                </div>
            ),
        },
    ];

    const isTalk = (doc) => doc?.kind === "toolboxTalk";
    const restoreLabel = (doc) => doc.restore?.mode === "newVersion" ? (isTalk(doc) ? "Restore as a new issue" : "Restore with a new version") : "Restore";
    const archivedColumns = [
        {
            title: "Document",
            dataIndex: "title",
            render: (v, doc) => (
                <div className="cmp-person">
                    <span className="cmp-person-name">
                        <button className="cmp-link" onClick={() => setRecipientsDocId(doc._id)} data-doc-name>{v}</button>
                        {isTalk(doc) ? <Tag color="blue" style={{ marginLeft: 6 }}>Toolbox talk</Tag> : null}
                        {!isTalk(doc) && doc.version > 1 ? <Tag style={{ marginLeft: 4 }}>v{doc.version}</Tag> : null}
                    </span>
                    <span className="cmp-muted">
                        {isTalk(doc)
                            ? `Issue ${doc.version || 1}${doc.recurrence && doc.recurrence !== "none" ? ` · ${RECURRENCE_LABEL[doc.recurrence] || doc.recurrence}` : ""}`
                            : doc.typeName && doc.typeName !== v ? doc.typeName : doc.fileName}
                    </span>
                </div>
            ),
        },
        { title: "Applies to", dataIndex: "appliesTo", render: (_, doc) => appliesToCell(doc) },
        { title: "Signed", dataIndex: "stats", render: (stats) => <span>{stats?.acknowledged || 0}</span> },
        { title: "Uploaded", dataIndex: "uploadedAt", render: (v, doc) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{doc.uploadedBy}</span></div> },
        { title: "Archived", dataIndex: "archivedAt", render: (v, doc) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{doc.archivedBy || "--"}</span></div> },
        {
            title: "Can it come back?",
            dataIndex: "restore",
            render: (restore, doc) => restore?.mode === "asIs"
                ? <Tag color="green">Yes, as it was</Tag>
                : <Tooltip title={restore?.reason}><Tag color={restore?.mode === "newVersion" ? "gold" : "default"}>{restore?.mode === "newVersion" ? (isTalk(doc) ? "As a new issue" : "With a new version") : "No"}</Tag></Tooltip>,
        },
        {
            title: "",
            dataIndex: "actions",
            align: "right",
            render: (_, doc) => (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Dropdown
                        trigger={["click"]}
                        placement="bottomRight"
                        menu={{
                            items: [
                                { key: "recipients", label: "Recipients" },
                                { key: "preview", label: "Preview" },
                                { type: "divider" },
                                doc.restore?.mode === "no"
                                    ? { key: "restore", label: <Tooltip title={doc.restore?.reason} placement="left">Restore</Tooltip>, disabled: true }
                                    : { key: "restore", label: restoreLabel(doc) },
                            ],
                            onClick: ({ key }) => {
                                if (key === "recipients") setRecipientsDocId(doc._id);
                                if (key === "preview") window.open(window.location.origin + "/user/preview?object=" + encodeURIComponent(doc.fileKey), "_blank");
                                if (key === "restore") setRestoreDoc(doc);
                            },
                        }}
                    >
                        <button type="button" className="doc-row-menu" aria-label="Document actions" data-doc-actions>
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                        </button>
                    </Dropdown>
                </div>
            ),
        },
    ];

    const talkColumns = [
        {
            title: "Toolbox talk",
            dataIndex: "title",
            render: (v, talk) => (
                <div className="cmp-person">
                    <span className="cmp-person-name">{v} <Tag style={{ marginLeft: 4 }}>Issue {talk.version}</Tag></span>
                    <span className="cmp-muted">{summariseAudience(talk.audience)}</span>
                </div>
            ),
        },
        {
            title: "Repeats",
            dataIndex: "recurrence",
            render: (v, talk) => (
                <div className="cmp-person">
                    <span>{RECURRENCE_LABEL[v] || "One-off"}</span>
                    {talk.nextIssueAt ? <span className="cmp-muted">Next {formatDate(talk.nextIssueAt)}</span> : null}
                </div>
            ),
        },
        { title: "Confirm by", dataIndex: "acknowledgementMethod", render: (v) => METHOD_LABEL[v] || "--" },
        { title: "This issue", dataIndex: "stats", render: (stats, talk) => <Progress stats={stats} onClick={() => setRecipientsDocId(talk._id)} /> },
        { title: "Overdue", dataIndex: "overdue", render: (_, talk) => talk.stats?.overdue ? <span className="cmp-overdue">{talk.stats.overdue}</span> : <span className="cmp-muted">0</span> },
        { title: "Sent", dataIndex: "uploadedAt", render: (v, talk) => <div className="cmp-person"><span>{formatDate(v)}</span><span className="cmp-muted">{talk.uploadedBy}</span></div> },
        {
            title: "",
            dataIndex: "actions",
            align: "right",
            render: (_, talk) => (
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                    <button className="sm-btn" onClick={() => setRecipientsDocId(talk._id)}>Recipients</button>
                    <button className="sm-btn" onClick={() => window.open(window.location.origin + "/user/preview?object=" + encodeURIComponent(talk.fileKey), "_blank")}>Preview</button>
                    <button className="sm-btn" onClick={() => setTalkDrawer({ open: true, editTalk: talk })}>Edit</button>
                    <Dropdown
                        trigger={["click"]}
                        menu={{
                            items: [
                                { key: "issue", label: "Send a new issue now", disabled: issuingTalk },
                                { type: "divider" },
                                { key: "archive", label: "Archive (stop the talk)" },
                            ],
                            onClick: ({ key }) => key === "issue" ? issueNow(talk) : archive(talk),
                        }}
                    >
                        <button className="sm-btn">More ▾</button>
                    </Dropdown>
                </div>
            ),
        },
    ];

    const siteColumns = [
        {
            title: "Site",
            dataIndex: "siteName",
            render: (v, row) => <button className="cmp-link" onClick={() => navigate(`/user/site-detail/${row.siteId}`)}>{v}</button>,
        },
        { title: "Documents", dataIndex: "documents" },
        {
            title: "Signed",
            dataIndex: "acknowledged",
            render: (_, row) => <Progress stats={{ total: row.assignments, acknowledged: row.acknowledged, pending: row.outstanding, overdue: row.overdue }} onClick={() => { setApplies("Site"); setSearch(row.siteName || ""); setTab("documents"); }} />,
        },
        { title: "Outstanding", dataIndex: "outstanding" },
        { title: "Overdue", dataIndex: "overdue", render: (v) => v ? <span className="cmp-overdue">{v}</span> : <span className="cmp-muted">0</span> },
        { title: "Complete", dataIndex: "completion", render: (v) => `${v}%` },
        {
            title: "",
            dataIndex: "pack",
            align: "right",
            render: (_, row) => (
                <Tooltip title="Every document for this site and who has signed it - for a client or auditor">
                    <button className="sm-btn" disabled={exporting} onClick={() => runAuditExport({ exportAudit, input: { objectType: "Site", entityId: row.siteId, format: "pdf" }, notify })}>Audit pack</button>
                </Tooltip>
            ),
        },
    ];

    if (!flagsLoading && !complianceEnabled) {
        return <PageNotFound />;
    }

    return (
        <div style={{ padding: "25px 48px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px", marginBottom: "5px" }}>
                <div className="grid-title">
                    Compliance
                    <span className="grid-subtitle">{` - ${totals?.documents || 0} ${(totals?.documents || 0) === 1 ? "document" : "documents"} officers must acknowledge`}</span>
                </div>
                {totals && (
                    <div className="ba-stat-strip">
                        <div className="ba-stat"><span className="ba-stat-num">{totals.completion}%</span><span className="ba-stat-lbl">Complete</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{totals.acknowledged.toLocaleString("en-GB")}</span><span className="ba-stat-lbl">Signed</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{totals.outstanding.toLocaleString("en-GB")}</span><span className="ba-stat-lbl">Outstanding</span></div>
                        <div className="ba-stat"><span className={`ba-stat-num${totals.overdue ? " cmp-overdue" : ""}`}>{totals.overdue.toLocaleString("en-GB")}</span><span className="ba-stat-lbl">Overdue</span></div>
                        <div className="ba-stat"><span className={`ba-stat-num${totals.reviewDue ? " cmp-overdue" : ""}`}>{totals.reviewDue}</span><span className="ba-stat-lbl">Review due</span></div>
                        <div className="ba-stat"><span className="ba-stat-num">{totals.expiring}</span><span className="ba-stat-lbl">Expiring soon</span></div>
                    </div>
                )}
            </div>

            <div className="setting-body-inner grid-tabs" style={{ padding: "10px 0px" }}>
                <Tabs
                    activeKey={tab}
                    onChange={setTab}
                    items={[
                        // { key: "overview", label: "Overview" },
                        { key: "documents", label: `Documents (${documents.length})` },
                        { key: "talks", label: `Toolbox Talks (${talks.length})` },
                        { key: "archived", label: `Archived (${archivedDocs.length})` },
                    ]}
                />
            </div>

            {tab === "archived" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Input
                                placeholder="Search archived documents..."
                                className="generic-input-control"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                suffix={<FontAwesomeIcon icon={faSearch} style={{ color: "#0091ae" }} />}
                            />
                            <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginBottom:'15px'}}/>} style={{ width: 300, marginTop:'0px' }} value={applies} onChange={setApplies} options={APPLIES_FILTERS} />
                        </div>
                        <span className="cmp-hint" style={{ maxWidth: 440, textAlign: "right" }}>Signatures stay on record. A document or talk comes back as it was only while it's current, in date and unchanged - otherwise with a new version or issue.</span>
                    </div>
                    <Table
                        className="curvedTable"
                        loading={archivedLoading}
                        columns={archivedColumns}
                        dataSource={archivedRows}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        locale={{ emptyText: "Nothing archived. Documents and toolbox talks you archive come here, with their signatures." }}
                    />
                </>
            ) : tab === "talks" ? (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <span className="cmp-hint">Short safety briefings officers read and acknowledge in the app. Repeating talks are re-issued automatically.</span>
                        <Button className="grid-filed-btn" onClick={() => setTalkDrawer({ open: true, editTalk: null })}>New toolbox talk</Button>
                    </div>
                    <Table
                        className="curvedTable"
                        loading={talksLoading || issuingTalk}
                        columns={talkColumns}
                        dataSource={talks.map((talk) => ({ ...talk, key: talk._id }))}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        locale={{ emptyText: "No toolbox talks yet - write one and it goes straight to officers' phones." }}
                    />
                </>
            ) : tab === "overview" ? (
                <div className="cmp-grid-2" style={{ marginTop: -10 }}>
                    <div>
                        <div className="cmp-panel-title">Sites</div>
                        <Table
                            className="curvedTable"
                            loading={overviewLoading}
                            columns={siteColumns}
                            dataSource={(overview?.bySite || []).map((row) => ({ ...row, key: row.siteId }))}
                            pagination={{ pageSize: 15, hideOnSinglePage: true }}
                            locale={{ emptyText: "No site documents need acknowledging yet. Open a site's Documents tab and switch on \"Officers must acknowledge\"." }}
                        />
                    </div>
                    <div>
                        <div className="cmp-panel-title">Due for review</div>
                        {(overview?.reviewDue || []).length === 0 ? (
                            <div className="cmp-empty">Nothing due for review in the next 30 days.</div>
                        ) : (
                            <Table
                                className="curvedTable"
                                columns={[
                                    { title: "Document", dataIndex: "title", render: (v, row) => <button className="cmp-link" onClick={() => setRecipientsDocId(row._id)}>{v}</button> },
                                    { title: "Applies to", dataIndex: "appliesTo", render: (v) => v?.label === "Company-wide" ? "Company-wide" : v?.name },
                                    { title: "Review by", dataIndex: "reviewDate", render: (v) => <span className="cmp-overdue">{formatDate(v)}</span> },
                                ]}
                                dataSource={(overview?.reviewDue || []).map((row) => ({ ...row, key: row._id }))}
                                pagination={false}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <>
                    <div className="cmp-toolbar" style={{ marginTop: -16 }}>
                        <div style={{ display: "flex", gap: 10,  }}>
                            <Input
                                placeholder="Search documents, sites, customers..."
                                className="generic-input-control"
                                // style={{ width: 300 }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                suffix={<FontAwesomeIcon icon={faSearch} style={{ color: "#0091ae" }} />}
                            />
                            <Select className="custom-select" suffixIcon={<CaretDownFilled style={{marginBottom:'15px'}}/>} style={{ width: 300, marginTop:'0px' }} value={applies} onChange={setApplies} options={APPLIES_FILTERS} />
                        </div>
                        <div style={{ display: "flex", gap: 10 }}>
                            <Dropdown
                                trigger={["click"]}
                                menu={{
                                    items: [
                                        { key: "pdf", label: `Audit pack (PDF)${applies !== "all" ? ` - ${APPLIES_FILTERS.find((f) => f.value === applies)?.label}` : ""}` },
                                        { key: "csv", label: `Spreadsheet (CSV)${applies !== "all" ? ` - ${APPLIES_FILTERS.find((f) => f.value === applies)?.label}` : ""}` },
                                    ],
                                    onClick: ({ key }) => runAuditExport({ exportAudit, input: { objectType: applies !== "all" ? applies : undefined, format: key }, notify }),
                                }}
                            >
                                <Button className="grid-outlined-btn" disabled={exporting}>{exporting ? "Preparing..." : "Export audit pack "} <CaretDownFilled/> </Button>
                            </Dropdown>
                            <button className="grid-filed-btn" onClick={() => setDrawer({ open: true, editDoc: null })}>New Company Document</button>
                        </div>
                    </div>
                    <Table
                        className="curvedTable"
                        loading={docsLoading || uploadingVersion}
                        columns={documentColumns}
                        dataSource={rows}
                        pagination={{ pageSize: 20, hideOnSinglePage: true }}
                        locale={{ emptyText: "No documents yet. Upload a company-wide document here, or switch on \"Officers must acknowledge\" on a site's documents." }}
                    />
                </>
            )}

            <input type="file" ref={versionInputRef} style={{ display: "none" }} onChange={onVersionFile} />

            {recipientsDocId && (
                <RecipientsDrawer
                    visible={!!recipientsDocId}
                    documentId={recipientsDocId}
                    // documents get a new version from the drawer; toolbox talks are re-issued instead
                    allowNewVersion={!talks.some((talk) => talk._id === recipientsDocId)}
                    // an archived one: restore from its drawer too
                    onRestore={(id) => { const doc = archivedDocs.find((d) => d._id === id); if (doc) { setRecipientsDocId(null); setRestoreDoc(doc); } }}
                    onClose={() => { setRecipientsDocId(null); refetchAll(); }}
                    onChanged={refetchAll}
                />
            )}

            {restoreDoc && (
                <RestoreDocumentModal
                    doc={restoreDoc}
                    onClose={() => setRestoreDoc(null)}
                    onRestored={async () => { setRecipientsDocId(null); await refetchAll(); }}
                />
            )}

            <ToolboxTalkDrawer
                visible={talkDrawer.open}
                editTalk={talkDrawer.editTalk}
                onClose={() => setTalkDrawer({ open: false, editTalk: null })}
                onSaved={refetchAll}
            />

            <ComplianceDocumentDrawer
                visible={drawer.open}
                editDoc={drawer.editDoc}
                onClose={() => setDrawer({ open: false, editDoc: null })}
                onSaved={refetchAll}
            />
        </div>
    );
};
